"use client"

import type React from "react"
import { useState, useRef, useEffect } from "react"
import { createPortal } from "react-dom"
import { Search, X, Plus, Loader2, Globe, Hash, Type, Camera, ChevronLeft } from "@/lib/icons"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"
import { logger } from "@/lib/logger"
import type { BooruSite } from "@/lib/booru-api"
import { useSettingsStore } from "@/lib/settings-store"
import { useTagAliasesStore } from "@/lib/tag-aliases-store"
import { formatPostCount, orderTagSuggestions, tagStartsWith } from "@/lib/tag-suggestions"
import { getTagCategory, getTagCategoryColor, getTagColor, getTagTextColor } from "@/lib/tag-categorizer"
import { useTagTaxonomyStore, type TagCategory } from "@/lib/tag-taxonomy-store"
import {
  buildAntecedentMap,
  buildPostCountMap,
  buildTaxonomyCategoryMap,
  mapRawTagCategory,
  normalizeSuggestionKey,
  toTaxonomyCategory,
} from "@/lib/booru-tag-suggestions"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { useTranslations } from "next-intl"
import { profileHref } from "@/lib/profile-url"
import { useDefaultProfileImages } from "@/lib/default-profile-images"
import { useTrendingTags } from "@/hooks/use-trending-tags"
import { TrendingTagBadges } from "@/components/trending-tag-badges"
import { useSourceStore } from "@/lib/source-store"

interface UserResult {
  id: string
  username: string
  display_name: string | null
  short_code: string | null
  avatar_url: string | null
  is_verified: boolean
  followers: number
}

interface SearchBarProps {
  tags: string[]
  negativeTags?: string[]
  onTagsChange: (tags: string[]) => void
  onNegativeTagsChange?: (tags: string[]) => void
  placeholder?: string
  className?: string
  activeSite?: BooruSite
  /** The sources currently selected: the trending list shown on an empty click combines them. Defaults to `activeSite`. */
  trendingSites?: readonly string[]
  mobileEndSlot?: React.ReactNode
}

/**
 * VisiSearch (the camera button: find tags by picture) is hidden for now so the bar can be judged without it (owner, 2026-10-14).
 * TO BRING IT BACK: set this to true. Nothing else was removed: the /visisearch page, its admin tools and its API all still exist.
 */
const SHOW_VISISEARCH_BUTTON = false

export function SearchBar({
  tags,
  negativeTags = [],
  onTagsChange,
  onNegativeTagsChange,
  placeholder = "Add tags...",
  className,
  activeSite = "gelbooru",
  trendingSites,
  mobileEndSlot,
}: SearchBarProps) {
  const tt = useTranslations("searchBar")
  const router = useRouter()
  const { avatarUrl: defaultAvatarUrl } = useDefaultProfileImages()
  const [inputValue, setInputValue] = useState("")
  // "@" first = look up people instead of tags (no tag starts with "@").
  const isUserSearch = inputValue.startsWith("@")
  const [userResults, setUserResults] = useState<UserResult[]>([])
  const [userStatus, setUserStatus] = useState<"idle" | "loading" | "done">("idle")
  const [inputMode, setInputMode] = useState<"positive" | "negative">("positive")
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [suggestionCategoryMap, setSuggestionCategoryMap] = useState<Record<string, TagCategory>>({})
  const [suggestionCounts, setSuggestionCounts] = useState<Record<string, number>>({})
  const [suggestionAliases, setSuggestionAliases] = useState<Record<string, string>>({})
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showVariants, setShowVariants] = useState(false)
  const [selectedTagForVariants, setSelectedTagForVariants] = useState<string>("")
  const [variants, setVariants] = useState<string[]>([])
  const [variantsStatus, setVariantsStatus] = useState<"idle" | "loading" | "none" | "error">("idle")
  const inputRef = useRef<HTMLInputElement>(null)
  // The dropdowns are drawn on <body>, not inside the bar. The header they sit in has its own backdrop-blur, and a
  // backdrop-filter nested inside another one cannot see the page behind it, so the blur silently did nothing
  // (same cause as the mini search bar's source menu). Position comes from the bar's own rectangle.
  const barRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [dropRect, setDropRect] = useState<{ left: number; top: number; width: number } | null>(null)

  const tagsScrollableMode = useSettingsStore((state) => state.tagsScrollableMode)
  const searchMode = useSettingsStore((state) => state.searchMode)
  const setSearchMode = useSettingsStore((state) => state.setSearchMode)
  const { getAliasesForTag, getAliasGroup } = useTagAliasesStore()

  useEffect(() => {
    if (!isUserSearch) {
      setUserResults([])
      setUserStatus("idle")
      return
    }
    const query = inputValue.slice(1).trim()
    if (!query) {
      setUserResults([])
      setUserStatus("idle")
      return
    }
    setUserStatus("loading")
    const controller = new AbortController()
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`/api/users/search?q=${encodeURIComponent(query)}&limit=20`, { signal: controller.signal })
        const data = await res.json()
        setUserResults(Array.isArray(data.users) ? data.users : [])
        setUserStatus("done")
      } catch (error) {
        if ((error as Error)?.name !== "AbortError") {
          setUserResults([])
          setUserStatus("done")
        }
      }
    }, 250)
    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [inputValue, isUserSearch])

  useEffect(() => {
    if (inputValue.length > 0 && !showVariants && !isUserSearch) {
      setLoading(true)
      const timer = setTimeout(async () => {
        try {
          logger.debug("SearchBar", `Fetching suggestions for "${inputValue}" from ${activeSite}`)

          let suggestions: string[] = []
          let categoryMap: Record<string, TagCategory> = {}
          let countMap: Record<string, number> = {}
          let aliasMap: Record<string, string> = {}
          const preferServerTagApi = activeSite === 'rule34' || activeSite === 'rule34.xxx'

          // Use direct browser autocomplete first for sites where cookies help bypass blocks.
          const directUrl = getDirectAutocompleteUrl(activeSite, inputValue)
          if (directUrl && !preferServerTagApi) {
            try {
              const directResponse = await fetch(directUrl, {
                mode: 'cors',
                credentials: 'include', // Include cookies for Cloudflare bypass
              })
              
              if (directResponse.ok) {
                const directData = await directResponse.json()
                const parsed = parseDirectAutocomplete(directData, activeSite)
                if (parsed.suggestions.length > 0) {
                  suggestions = parsed.suggestions
                  categoryMap = parsed.categoryMap
                  countMap = parsed.countMap
                  aliasMap = parsed.aliasMap
                  logger.info("SearchBar", `Client-side direct request succeeded: ${suggestions.length} suggestions`)
                }
              }
            } catch (directError) {
              logger.warn("SearchBar", "Client-side direct request failed, trying server-side", directError)
            }
          }

          // Server API returns structured tag categories, including Rule34 DAPI types.
          if (suggestions.length === 0) {
            try {
              const response = await fetch(
                `/api/booru/tags?q=${encodeURIComponent(inputValue)}&site=${activeSite}&limit=15`,
              )
              
              const data = await response.json()
              
              if (response.ok && data.suggestions && data.suggestions.length > 0) {
                suggestions = data.suggestions
                categoryMap = buildTaxonomyCategoryMap(data.tagSuggestions)
                countMap = buildPostCountMap(data.tagSuggestions)
                aliasMap = buildAntecedentMap(data.tagSuggestions)
                logger.info("SearchBar", `Server-side succeeded: ${suggestions.length} suggestions`)
              }
            } catch (serverError) {
              logger.warn("SearchBar", "Server-side also failed", serverError)
            }
          }

          // For Rule34, keep direct autocomplete as the fallback after DAPI/server attempts.
          if (suggestions.length === 0 && directUrl && preferServerTagApi) {
            try {
              const directResponse = await fetch(directUrl, {
                mode: 'cors',
                credentials: 'include',
              })

              if (directResponse.ok) {
                const directData = await directResponse.json()
                const parsed = parseDirectAutocomplete(directData, activeSite)
                if (parsed.suggestions.length > 0) {
                  suggestions = parsed.suggestions
                  categoryMap = parsed.categoryMap
                  countMap = parsed.countMap
                  aliasMap = parsed.aliasMap
                  logger.info("SearchBar", `Client-side direct fallback succeeded: ${suggestions.length} suggestions`)
                }
              }
            } catch (directError) {
              logger.warn("SearchBar", "Client-side direct fallback failed", directError)
            }
          }

          // Last resort: Try local database if available (Rule34 only)
          if (suggestions.length === 0 && (activeSite === 'rule34' || activeSite === 'rule34.xxx')) {
            const localSuggestions = getLocalTagSuggestions(inputValue)
            if (localSuggestions.length > 0) {
              suggestions = localSuggestions
              categoryMap = buildLocalCategoryMap(localSuggestions)
              logger.info("SearchBar", `Using local database fallback: ${suggestions.length} suggestions`)
            }
          }
          
          // Whatever source answered (site autocomplete, server API, local database),
          // show only tags that START with what was typed, exact match first, then
          // A-Z — the same rule VisiSearch uses. Sites return a short popularity-ranked
          // list, so it's fetched at 15 (not 10) to give the A-Z sort something to work with.
          suggestions = orderTagSuggestions(inputValue, suggestions)

          // Always include the user's input as the first suggestion
          const allSuggestions = [inputValue, ...suggestions.filter((s: string) => s !== inputValue)]
          
          // Remove duplicates while preserving order
          const uniqueSuggestions = Array.from(new Set(allSuggestions))

          setSuggestions(uniqueSuggestions)
          setSuggestionCategoryMap(categoryMap)
          setSuggestionCounts(countMap)
          setSuggestionAliases(aliasMap)
          setShowSuggestions(true)

          logger.info("SearchBar", `Got ${uniqueSuggestions.length} suggestions for "${inputValue}"`)
        } catch (error) {
          const errorMsg = error instanceof Error ? error.message : String(error)
          logger.error("SearchBar", "Failed to fetch tag suggestions", errorMsg)
          
          // Still show the user's input as a suggestion even if API fails
          setSuggestions([inputValue])
          setSuggestionCategoryMap({})
          setSuggestionCounts({})
          setSuggestionAliases({})
          setShowSuggestions(true)
        } finally {
          setLoading(false)
        }
      }, 300) // 300ms debounce
      return () => clearTimeout(timer)
    } else {
      setSuggestions([])
      setSuggestionCategoryMap({})
      setSuggestionCounts({})
      setSuggestionAliases({})
      setShowSuggestions(false)
      setLoading(false)
    }
  }, [inputValue, activeSite, showVariants, isUserSearch])

  const [userPanelDismissed, setUserPanelDismissed] = useState(false)
  useEffect(() => { setUserPanelDismissed(false) }, [inputValue])
  const showUserPanel = isUserSearch && inputValue.length > 1 && userStatus !== "idle" && !userPanelDismissed
  // An empty, focused search box offers what is trending on the selected sites; the moment something is typed it goes away
  // and normal tag search takes over.
  const [showTrending, setShowTrending] = useState(false)
  const trending = useTrendingTags(trendingSites && trendingSites.length > 0 ? trendingSites : [activeSite], {
    enabled: showTrending && !showVariants && !isUserSearch,
    limit: 16,
  })
  const trendingTags = trending.tags.filter((tag) => !tags.includes(tag.tag) && !negativeTags.includes(tag.tag))
  const trendingVisible = showTrending && inputValue === "" && !showVariants && !isUserSearch && trendingTags.length > 0
  const getSourceConfig = useSourceStore((state) => state.getSource)
  const trendingSourceNames = trending.sources.map((id) => getSourceConfig(id)?.name ?? id).join(", ")
  const dropdownOpen = showUserPanel || showVariants || (showSuggestions && suggestions.length > 0) || trendingVisible
  useEffect(() => {
    if (!dropdownOpen) return
    const place = () => {
      const rect = barRef.current?.getBoundingClientRect()
      if (rect) setDropRect({ left: rect.left, top: rect.bottom + 8, width: rect.width })
    }
    place()
    window.addEventListener("resize", place)
    window.addEventListener("scroll", place, true)
    return () => {
      window.removeEventListener("resize", place)
      window.removeEventListener("scroll", place, true)
    }
  }, [dropdownOpen])

  const closeVariants = () => {
    setShowVariants(false)
    setVariants([])
    setVariantsStatus("idle")
    setSelectedTagForVariants("")
  }

  useEffect(() => {
    if (!showVariants) return
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (panelRef.current?.contains(target) || barRef.current?.contains(target)) return
      closeVariants()
    }
    document.addEventListener("mousedown", onDown)
    return () => document.removeEventListener("mousedown", onDown)
  }, [showVariants])

  const showLanguageVariants = async (tag: string) => {
    setSelectedTagForVariants(tag)
    setShowVariants(true)
    setLoading(true)

    // Update input value and keep dropdown open
    setInputValue(tag)
    inputRef.current?.focus()

    setVariants([])
    setVariantsStatus("loading")
    try {
      // Other names for exactly this tag: its aliases and native-script spellings (e.g. the Japanese name).
      const response = await fetch(`/api/booru/tags?q=${encodeURIComponent(tag)}&site=${activeSite}&variants=1`)
      const data = await response.json()
      const found: string[] = response.ok && Array.isArray(data.variants) ? data.variants : []
      setVariants(found)
      setVariantsStatus(found.length > 0 ? "idle" : "none")
    } catch (error) {
      logger.error("SearchBar", "Failed to fetch variants", String(error))
      setVariantsStatus("error")
    } finally {
      setLoading(false)
    }
  }

  const addTag = (tag: string, asNegative = false) => {
    console.log('[v0] SearchBar addTag called', { tag, asNegative, currentTags: tags, currentNegativeTags: negativeTags })
    const cleanTag = normalizeSuggestionKey(tag)
    const category = suggestionCategoryMap[cleanTag] || suggestionCategoryMap[tag.trim().toLowerCase()]
    const currentNegativeTags = Array.isArray(negativeTags) ? [...negativeTags] : []
    const currentTags = Array.isArray(tags) ? [...tags] : []
    if (cleanTag && category) {
      useTagTaxonomyStore.getState().categorizeTag(cleanTag, category, undefined, tag)
    }

    if (asNegative) {
      if (cleanTag && !currentNegativeTags.includes(cleanTag)) {
        logger.info("SearchBar", `Added negative tag: ${cleanTag}`)
        const newNegativeTags = [...currentNegativeTags, cleanTag]
        console.log("[v0] Adding negative tag:", cleanTag, "New array:", newNegativeTags)
        console.log("[v0] Calling onNegativeTagsChange with:", newNegativeTags)
        onNegativeTagsChange?.(newNegativeTags)
      } else {
        console.log('[v0] Negative tag not added - already exists or empty', { cleanTag, exists: currentNegativeTags.includes(cleanTag) })
      }
    } else {
      if (cleanTag && !currentTags.includes(cleanTag)) {
        logger.info("SearchBar", `Added tag: ${cleanTag}`)
        const newTags = [...currentTags, cleanTag]
        console.log("[v0] Calling onTagsChange with:", newTags)
        onTagsChange(newTags)
      } else {
        console.log('[v0] Tag not added - already exists or empty', { cleanTag, exists: currentTags.includes(cleanTag) })
      }
    }
    setInputValue("")
    setShowSuggestions(false)
    closeVariants()
    setInputMode("positive")
    inputRef.current?.focus()
  }

  const removeTag = (tagToRemove: string) => {
    logger.info("SearchBar", `Removed tag: ${tagToRemove}`)
    onTagsChange(tags.filter((tag) => tag !== tagToRemove))
  }

  const removeNegativeTag = (tagToRemove: string) => {
    logger.info("SearchBar", `Removed negative tag: ${tagToRemove}`)
    const filtered = (negativeTags || []).filter((tag) => tag !== tagToRemove)
    console.log("[v0] Removing negative tag:", tagToRemove, "New array:", filtered)
    onNegativeTagsChange?.(filtered)
  }

  const cycleAlias = (tag: string) => {
    const aliases = getAliasesForTag(tag)
    if (aliases.length <= 1) return

    const currentIndex = aliases.indexOf(tag)
    const nextIndex = (currentIndex + 1) % aliases.length
    const nextAlias = aliases[nextIndex]

    const newTags = tags.map((t) => (t === tag ? nextAlias : t))
    onTagsChange(newTags)

    logger.info("SearchBar", `Cycled tag alias from ${tag} to ${nextAlias}`)
  }

  const openUser = (u: UserResult) => {
    setInputValue("")
    setUserResults([])
    router.push(profileHref(u.short_code, u.username, u.id))
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    console.log('[v0] SearchBar handleKeyDown', { key: e.key, inputValue, inputMode })
    if (e.key === "Enter" && isUserSearch) {
      // "@" searches people: Enter opens the top match (never turns "@name" into a tag).
      e.preventDefault()
      if (userResults[0]) openUser(userResults[0])
    } else if (e.key === "Enter" && inputValue) {
      e.preventDefault()
      const isNegative = inputValue.startsWith("-")
      const cleanInput = isNegative ? inputValue.slice(1) : inputValue
      console.log('[v0] Enter pressed - calling addTag', { cleanInput, isNegative })
      addTag(cleanInput, isNegative)
    } else if (e.key === "Escape") {
      closeVariants()
      setShowSuggestions(false)
      setShowTrending(false)
      setUserPanelDismissed(true)
    } else if (e.key === "-" && inputValue === "") {
      e.preventDefault()
      setInputMode("negative")
    } else if (e.key === "+" && inputValue === "") {
      e.preventDefault()
      setInputMode("positive")
    } else if (e.key === "Backspace" && !inputValue && tags.length > 0 && inputMode === "positive") {
      removeTag(tags[tags.length - 1])
    } else if (e.key === "Backspace" && !inputValue && negativeTags.length > 0 && inputMode === "negative") {
      removeNegativeTag(negativeTags[negativeTags.length - 1])
    }
  }

  return (
    <div ref={barRef} className={cn("relative", className)}>
      <div className="flex items-center gap-1.5 glass-strong rounded-lg focus-within:ring-2 focus-within:ring-ring/50 shadow-sm border border-border/50 bg-secondary/20 px-1 py-[5px]">
        {/* Left anchors — always visible */}
        <Search className="h-4 w-4 text-muted-foreground flex-shrink-0" />

        {/* Search Mode Toggle */}
        <Button
          variant="ghost"
          size="sm"
          className="h-5 w-5 p-0 hover:bg-muted flex-shrink-0"
          onClick={() => setSearchMode(searchMode === "tags" ? "title" : "tags")}
          title={searchMode === "tags" ? "Switch to title search" : "Switch to tag search"}
        >
          {searchMode === "tags" ? (
            <Hash className="h-3.5 w-3.5 text-muted-foreground" />
          ) : (
            <Type className="h-3.5 w-3.5 text-muted-foreground" />
          )}
        </Button>

        {/* Scrollable / wrapping middle — tags + input */}
        <div
          className={cn(
            "flex-1 flex items-center gap-1.5 min-w-0",
            tagsScrollableMode ? "overflow-x-auto scrollbar-hide flex-nowrap" : "flex-wrap",
          )}
        >
          {tags.map((tag) => {
            const aliasGroup = getAliasGroup(tag)
            const hasAliases = aliasGroup && aliasGroup.aliases.length > 1
            const category = getTagCategory(tag)
            const colorClass = getTagColor(category)
            const textColorClass = getTagTextColor(category)

            return (
              <div key={tag} className="flex items-center gap-1 flex-shrink-0 group">
                <Badge
                  variant="secondary"
                  className={cn(
                    "gap-1 pl-2 pr-1 h-6 text-xs cursor-pointer hover:opacity-90 transition-all border",
                    colorClass,
                    textColorClass,
                  )}
                  onClick={() => hasAliases && cycleAlias(tag)}
                  title={
                    hasAliases
                      ? `Click to cycle through ${aliasGroup.aliases.length} variants: ${aliasGroup.aliases.join(", ")}`
                      : ""
                  }
                >
                  {tag.replace(/_/g, " ")}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-4 w-4 p-0 hover:bg-transparent"
                    onClick={(e) => {
                      e.stopPropagation()
                      removeTag(tag)
                    }}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </Badge>
                {hasAliases && (
                  <button
                    onClick={() => cycleAlias(tag)}
                    className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded hover:bg-muted/80 transition-colors font-semibold cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity"
                    title={`Click to cycle: ${aliasGroup.aliases.join(" → ")}`}
                  >
                    +{aliasGroup.aliases.length - 1}
                  </button>
                )}
              </div>
            )
          })}

          {negativeTags.map((tag) => (
            <Badge
              key={`neg-${tag}`}
              variant="destructive"
              className="gap-1 pl-2 pr-1 h-6 text-xs bg-destructive/30 text-destructive flex-shrink-0 border border-destructive/50"
            >
              -{tag.replace(/_/g, " ")}
              <Button
                variant="ghost"
                size="sm"
                className="h-4 w-4 p-0 hover:bg-transparent"
                onClick={() => removeNegativeTag(tag)}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          ))}

          {inputValue && !isUserSearch && (
            <span className="text-xs text-muted-foreground flex-shrink-0">{inputMode === "negative" ? "−" : "+"}</span>
          )}

          <Input
            ref={inputRef}
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value)
              setShowTrending(e.target.value === "")
            }}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (inputValue.length === 0 && !showVariants) setShowTrending(true)
              return (inputValue.length > 0 || showVariants) && setShowSuggestions(!showVariants)
            }}
            onBlur={() => {
              if (!showVariants) {
                setTimeout(() => {
                  setShowSuggestions(false)
                  setShowTrending(false)
                }, 200)
              }
            }}
            placeholder={
              tags.length === 0 && negativeTags.length === 0
                ? searchMode === "tags"
                  ? `${placeholder.replace(/[.…]+$/, "")}… ${tt("findUsersHint")}`
                  : `${tt("titleSearchPlaceholder")}… ${tt("findUsersHint")}`
                : ""
            }
            className="flex-1 min-w-[60px] border-0 p-0 h-6 focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent text-sm text-foreground"
          />

          {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground flex-shrink-0" />}
        </div>

        {/* Right anchors — always visible, never scroll off or wrap */}
        {SHOW_VISISEARCH_BUTTON && (
          <Link
            href="/visisearch"
            className="h-6 w-6 flex items-center justify-center hover:bg-indigo-500/20 flex-shrink-0 rounded-full transition-colors"
            title={tt("visiSearchFindTagsByImage")}
          >
            <Camera className="h-3.5 w-3.5 text-muted-foreground" />
          </Link>
        )}

        {/* Mobile-only slot — renders content (e.g. back button) inside the bar on narrow screens */}
        {mobileEndSlot && (
          <div className="sm:hidden shrink-0">{mobileEndSlot}</div>
        )}
      </div>

      {dropRect &&
        typeof document !== "undefined" &&
        dropdownOpen &&
        createPortal(
          <div
            ref={panelRef}
            style={{
              position: "fixed",
              left: dropRect.left,
              top: dropRect.top,
              width: dropRect.width,
              // People results: about five rows at a time, never more than 45% of the screen (a phone with the keyboard up).
              ...(showUserPanel ? { maxHeight: "min(15rem, 45dvh)", touchAction: "pan-y" } : {}),
            }}
            className={cn(
              "z-[70] rounded-lg p-2 shadow-lg border border-border/50 bg-background/70 backdrop-blur-xl overflow-y-auto overscroll-contain",
              !showUserPanel && "max-h-80",
            )}
          >
            {showUserPanel ? (
              <div className="space-y-1">
                <div className="px-3 py-1 text-xs font-semibold text-muted-foreground">{tt("usersHeading")}</div>
                {userStatus === "loading" && userResults.length === 0 && (
                  <div className="px-3 py-2 text-xs text-muted-foreground italic">{tt("variantsLoading")}</div>
                )}
                {userStatus === "done" && userResults.length === 0 && (
                  <div className="px-3 py-2 text-xs text-muted-foreground italic">{tt("usersNone")}</div>
                )}
                {userResults.map((u) => (
                  <button
                    key={u.id}
                    className="w-full min-h-11 flex items-center gap-2.5 text-left px-2 py-1.5 rounded-md hover:bg-primary/20 focus-visible:bg-primary/20 focus-visible:outline-none transition-colors text-foreground"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => openUser(u)}
                  >
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={u.avatar_url || defaultAvatarUrl}
                      alt=""
                      onError={(e) => {
                        const img = e.currentTarget
                        if (img.src !== defaultAvatarUrl) img.src = defaultAvatarUrl
                      }}
                      className="h-8 w-8 rounded-full bg-muted object-cover shrink-0"
                    />
                    <span className="flex-1 min-w-0">
                      <span className="block truncate text-sm font-medium">{u.display_name || u.username}</span>
                      <span className="block truncate text-[11px] text-muted-foreground">
                        @{u.username} · {tt("usersFollowers", { count: u.followers })}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            ) : showVariants ? (
              <div className="space-y-1">
                <div className="flex items-center gap-1 px-1 py-1">
                  <button
                    className="p-1.5 rounded-md hover:bg-accent/50 transition-colors text-foreground"
                    onClick={() => {
                      closeVariants()
                      setShowSuggestions(true)
                      inputRef.current?.focus()
                    }}
                    title={tt("variantsBack")}
                    aria-label={tt("variantsBack")}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <div className="flex-1 text-xs text-muted-foreground font-semibold truncate">
                    {tt("languageVariants")}: {selectedTagForVariants.replace(/_/g, " ")}
                  </div>
                  <button
                    className="p-1.5 rounded-md hover:bg-accent/50 transition-colors text-muted-foreground"
                    onClick={closeVariants}
                    title={tt("variantsClose")}
                    aria-label={tt("variantsClose")}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                {variantsStatus === "loading" && (
                  <div className="px-3 py-2 text-xs text-muted-foreground italic">{tt("variantsLoading")}</div>
                )}
                {variantsStatus === "none" && (
                  <div className="px-3 py-2 text-xs text-muted-foreground italic">{tt("variantsNone")}</div>
                )}
                {variantsStatus === "error" && (
                  <div className="px-3 py-2 text-xs text-muted-foreground italic">{tt("variantsError")}</div>
                )}
                {variants.map((variant) => (
                  <button
                    key={variant}
                    className="w-full text-left px-3 py-2 rounded-md hover:bg-primary/20 focus-visible:bg-primary/20 focus-visible:outline-none transition-colors text-xs text-foreground"
                    onClick={() => addTag(variant, false)}
                  >
                    <Plus className="h-3 w-3 inline mr-2" />
                    {variant.replace(/_/g, " ")}
                  </button>
                ))}
              </div>
            ) : trendingVisible ? (
              <div className="space-y-2">
                <div className="px-1 text-xs font-semibold text-muted-foreground">
                  {trendingSourceNames ? tt("trendingOn", { sources: trendingSourceNames }) : tt("trendingNow")}
                </div>
                <TrendingTagBadges
                  tags={trendingTags}
                  onPick={(picked) => {
                    useTagTaxonomyStore.getState().categorizeTag(picked.tag, picked.category, undefined, picked.tag)
                    addTag(picked.tag, false)
                  }}
                />
              </div>
            ) : (
              <div className="space-y-1">
                {suggestions.map((suggestion) => (
                  <div key={suggestion} className="flex gap-1 items-center">
                    <button
                      className="flex-1 flex items-center gap-2 text-left px-3 py-2 rounded-md hover:bg-primary/20 focus-visible:bg-primary/20 focus-visible:outline-none transition-colors text-xs text-foreground"
                      onClick={() => addTag(suggestion, false)}
                    >
                      <Plus className="h-3 w-3 shrink-0" />
                      <span className={cn("flex-1 truncate", tagTypeColor(suggestionCategoryMap[normalizeSuggestionKey(suggestion)]))}>
                        {suggestionAliases[normalizeSuggestionKey(suggestion)] && (
                          <span className="text-muted-foreground">
                            {suggestionAliases[normalizeSuggestionKey(suggestion)].replace(/_/g, " ")} →{" "}
                          </span>
                        )}
                        {suggestion.replace(/_/g, " ")}
                      </span>
                      {suggestionCounts[normalizeSuggestionKey(suggestion)] !== undefined && (
                        <span className="shrink-0 tabular-nums text-muted-foreground">
                          {formatPostCount(suggestionCounts[normalizeSuggestionKey(suggestion)])}
                        </span>
                      )}
                    </button>
                    <button
                      className="px-2 py-2 rounded-md hover:bg-blue-500/20 transition-colors text-xs text-blue-500"
                      onClick={(e) => {
                        e.stopPropagation()
                        showLanguageVariants(suggestion)
                      }}
                      title={tt("showLanguageVariants")}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      <Globe className="h-4 w-4" />
                    </button>
                    <button
                      className="px-2 py-2 rounded-md hover:bg-destructive/20 transition-colors text-xs text-destructive"
                      onClick={() => addTag(suggestion, true)}
                      title={tt("addAsNegativeTag")}
                    >
                      −
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>,
          document.body,
        )}
    </div>
  )
}


/** Colour for a suggestion by tag type (artist red, character green, series purple, meta blue), the same scheme the tag
 * pills use. General tags stay the normal text colour. */
function tagTypeColor(category?: TagCategory): string {
  return category && category !== "general" ? getTagCategoryColor(category) : ""
}

// Helper functions for direct autocomplete (client-side fallback)
function getDirectAutocompleteUrl(site: string, query: string): string | null {
  const encoded = encodeURIComponent(query)
  
  switch (site) {
    case 'rule34':
    case 'rule34.xxx':
      return `https://rule34.xxx/public/autocomplete.php?q=${encoded}`
    case 'gelbooru':
      return `https://gelbooru.com/index.php?page=autocomplete2&term=${encoded}&type=tag_query&limit=15`
    case 'danbooru':
      return `https://danbooru.donmai.us/autocomplete.json?search[query]=${encoded}&search[type]=tag_query&limit=15`
    case 'e621':
      return `https://e621.net/tags/autocomplete.json?search[name_matches]=${encoded}&limit=15`
    case 'e926':
      return `https://e926.net/tags/autocomplete.json?search[name_matches]=${encoded}&limit=15`
    case 'safebooru':
      return `https://safebooru.org/autocomplete.php?q=${encoded}`
    default:
      return null
  }
}

function parseDirectAutocomplete(data: any, site: string): { suggestions: string[]; categoryMap: Record<string, TagCategory>; countMap: Record<string, number>; aliasMap: Record<string, string> } {
  if (!Array.isArray(data)) return { suggestions: [], categoryMap: {}, countMap: {}, aliasMap: {} }
  
  switch (site) {
    case 'rule34':
    case 'rule34.xxx':
    case 'gelbooru':
    case 'safebooru':
      // Format: [{ label, value, type }]
      return {
        suggestions: data.map((item: any) => item.value || item.label).filter(Boolean),
        categoryMap: buildTaxonomyCategoryMap(data),
        countMap: buildPostCountMap(data),
        aliasMap: buildAntecedentMap(data),
      }
    
    case 'danbooru':
      // Format: [{ type, label, value, category }]
      return {
        suggestions: data.map((item: any) => item.value || item.label).filter(Boolean),
        categoryMap: buildTaxonomyCategoryMap(data),
        countMap: buildPostCountMap(data),
        aliasMap: buildAntecedentMap(data),
      }
    
    case 'e621':
    case 'e926':
      // Format: [{ id, name, post_count, category }]
      return {
        suggestions: data.map((item: any) => item.name).filter(Boolean),
        categoryMap: buildTaxonomyCategoryMap(data),
        countMap: buildPostCountMap(data),
        aliasMap: buildAntecedentMap(data),
      }
    
    default:
      return { suggestions: [], categoryMap: {}, countMap: {}, aliasMap: {} }
  }
}

function buildLocalCategoryMap(tags: string[]): Record<string, TagCategory> {
  if (typeof window === 'undefined') return {}
  const database = (window as any).__rule34TagDatabase
  if (!database) return {}

  const categoryMap: Record<string, TagCategory> = {}
  for (const tag of tags) {
    const entry = database[tag] || database[normalizeSuggestionKey(tag)]
    const category = typeof entry === "string" ? entry : entry?.category ?? entry?.type
    categoryMap[normalizeSuggestionKey(tag)] = toTaxonomyCategory(mapRawTagCategory(category))
  }

  return categoryMap
}

// Get tag suggestions from local Rule34 database (instant, no network)
function getLocalTagSuggestions(query: string, limit: number = 15): string[] {
  if (typeof window === 'undefined') return []
  
  const database = (window as any).__rule34TagDatabase
  if (!database) return []
  
  // Tags that START with the query (same rule as everywhere else). This used to
  // collect the first `limit` tags that merely contained the query, in database
  // order, and only then sort them — so real prefix matches later in the
  // database were never even considered. Collect all prefix matches first
  // (capped), then rank and cut.
  const matches: string[] = []
  for (const tag in database) {
    if (matches.length >= 500) break
    if (tagStartsWith(query, tag)) matches.push(tag)
  }

  return orderTagSuggestions(query, matches, limit)
}
